import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useCatalog } from '../../hooks/useCatalog'
import CatalogNav from '../../components/catalog/CatalogNav'
import DimensionGroupCard from '../../components/catalog/DimensionGroupCard'

function SkeletonList({ count = 4 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 h-40 animate-pulse">
          <div className="h-4 w-1/3 bg-slate-200 rounded mb-3"></div>
          <div className="h-3 w-1/2 bg-slate-100 rounded mb-6"></div>
          <div className="h-3 w-full bg-slate-100 rounded mb-2"></div>
          <div className="h-3 w-5/6 bg-slate-100 rounded"></div>
        </div>
      ))}
    </div>
  )
}

function NotFoundState() {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <p className="text-sm text-slate-500">Dimension not found.</p>
      <Link
        to="/catalog"
        className="mt-4 inline-flex items-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Back to catalog
      </Link>
    </div>
  )
}

export default function DimensionView() {
  const { dimensionSlug } = useParams()
  const { fetchDimensionView } = useCatalog()

  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setNotFound(false)
    setPayload(null)

    fetchDimensionView(dimensionSlug)
      .then((data) => { if (!cancelled) setPayload(data) })
      .catch((err) => {
        if (cancelled) return
        if (err?.status === 404) {
          setNotFound(true)
        } else {
          setError(err.message || 'Failed to load dimension view')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [dimensionSlug, fetchDimensionView])

  const dimension = payload?.dimension
  const groups    = payload?.groups || []

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
          {dimension?.name || dimensionSlug?.replace(/_/g, ' ')}
        </h2>
        {dimension?.description ? (
          <p className="mt-1 text-sm text-slate-500 max-w-3xl">{dimension.description}</p>
        ) : (
          <p className="mt-1 text-sm text-slate-500">
            Entries grouped by the values of this dimension.
          </p>
        )}
      </div>

      <CatalogNav />

      {loading && <SkeletonList count={4} />}

      {notFound && !loading && <NotFoundState />}

      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && !notFound && groups.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm text-slate-500">
            No values yet for this dimension. Entries will show up here once extraction surfaces values under this facet.
          </p>
        </div>
      )}

      {groups.length > 0 && (
        <div className="space-y-4">
          {groups.map((group) => (
            <DimensionGroupCard
              key={group.value.id}
              group={group}
              dimensionSlug={dimensionSlug}
            />
          ))}
        </div>
      )}
    </div>
  )
}
